import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Badge } from '../components/ui/Badge';
import CreateAgentChoiceModal from '../components/ui/CreateAgentChoiceModal';
import { normalizeRole } from '../config/permissions';

function Agents() {
  const { can, user } = useAuth();
  // Managers may ONLY create child agents (never top-level). Used to force the
  // create-agent modal straight into the child flow and hide the scratch option.
  const isManagerOnly = normalizeRole(user?.role) === 'manager';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  // scope: 'all' | 'mine' | 'org'
  const [scope, setScope] = useState('all');

  useEffect(() => {
    fetchAgents();
  }, [scope]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/agents');
      let data = response.data || [];
      // Client-side scope filtering using owner_salesperson_id
      if (scope === 'mine') {
        const myProfileId = user?.salesperson_profile_id;
        data = myProfileId ? data.filter(a => a.owner_salesperson_id === myProfileId) : [];
      } else if (scope === 'org') {
        data = data.filter(a => !a.owner_salesperson_id);
      }
      setAgents(data);
    } catch (error) {
      toast.error(t('errors.failedFetchAgents'));
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await api.delete(`/agents/${agentId}`);
      toast.success(t('success.agentDeleted'));
      fetchAgents();
      setShowDeleteModal(false);
      setSelectedAgent(null);
    } catch (error) {
      toast.error(t('errors.failedDeleteAgent'));
      console.error('Error deleting agent:', error);
    }
  };

  const handleToggleAgentStatus = async (agentId, currentStatus) => {
    try {
      await api.put(`/agents/${agentId}`, {
        is_active: !currentStatus,
      });
      toast.success(!currentStatus ? t('success.agentActivated') : t('success.agentDeactivated'));
      fetchAgents();
    } catch (error) {
      toast.error(t('errors.failedUpdateAgent'));
      console.error('Error updating agent:', error);
    }
  };

  const openDeleteModal = (agent) => {
    setSelectedAgent(agent);
    setShowDeleteModal(true);
  };

  // ── Hierarchy grouping ────────────────────────────────────────────────────
  // parentAgents: top-level agents (no parent_agent_id) — shown at full size
  // childMap: parentId → child agents — shown indented below their parent
  // orphans: have parent_agent_id but parent is not in the current list
  const agentIndex = Object.fromEntries(agents.map(a => [a.id, a]));
  const parentAgents = agents.filter(a => !a.parent_agent_id).sort((a, b) => a.name.localeCompare(b.name));
  const childMap = {};
  const orphans = [];
  agents.forEach(a => {
    if (!a.parent_agent_id) return;
    if (agentIndex[a.parent_agent_id]) {
      childMap[a.parent_agent_id] = childMap[a.parent_agent_id] || [];
      childMap[a.parent_agent_id].push(a);
    } else {
      orphans.push(a);
    }
  });
  // Top-level: parents (each followed by their children) + orphans
  const topLevel = [...parentAgents, ...orphans];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const AgentCard = ({ agent, isChild = false, parentName = '' }) => {
    // Managers may only edit their child agents, never a parent/father agent.
    // Hide the edit pencil on parent cards for manager-only users (backend
    // enforces a 403 guard regardless; this keeps the UI honest).
    const isParent = !agent.parent_agent_id;
    const canEditThisAgent = !(isManagerOnly && isParent);
    return (
    <div
      className={`card flex flex-col hover:shadow-md transition-shadow ${isChild ? 'pl-4 border-l-4 border-l-blue-500 text-sm bg-indigo-50 !p-3' : ''}`}
      style={isChild ? { backgroundColor: 'var(--child-card-bg)' } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center min-w-0 flex-1">
          <div className="flex-shrink-0">
            <div className={`${isChild ? 'h-7 w-7' : 'h-10 w-10'} bg-primary-100 rounded-xl flex items-center justify-center`}>
              <UserGroupIcon className={`${isChild ? 'h-4 w-4' : 'h-6 w-6'} text-primary-600`} />
            </div>
          </div>
          <div className="ml-4 min-w-0">
            {isChild && (
              <div className="text-xs text-indigo-700 font-semibold mb-1">
                Child of: {parentName || '—'}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-medium text-foreground truncate">{agent.name}</h3>
              {!isChild && agent.parent_agent_id && !agentIndex[agent.parent_agent_id] && (
                <Badge variant="warning" className="text-xs">
                  ⚠️ {t('agents.orphan', 'huérfano')}
                </Badge>
              )}
            </div>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {t('agents.voice')}: {agent.tts_provider === 'elevenlabs' ? t('agents.aiVoice', 'AI Voice') : agent.voice}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {agent.is_shared && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {t('agents.shared')}
            </span>
          )}
          <span className={`status-badge ${agent.is_active ? 'status-active' : 'status-inactive'}`}>
            {agent.is_active ? t('common.active') : t('common.inactive')}
          </span>
        </div>
      </div>

      {!isChild && <div className="mt-4">
        <div className="text-sm" style={{ color: 'var(--muted)' }}>
          <p><span className="font-medium">{t('agents.language')}:</span> {agent.language}</p>
          <p><span className="font-medium">{t('agents.tools')}:</span> {agent.tools.length}</p>
          <p><span className="font-medium">{t('agents.sensitivity')}:</span> {
            agent.sensitivity_preset === 'responsive' ? t('agents.sensitivityResponsive') :
            agent.sensitivity_preset === 'balanced' ? t('agents.sensitivityBalanced') :
            agent.sensitivity_preset === 'conservative' ? t('agents.sensitivityConservative') :
            agent.sensitivity_preset === 'custom_server' ? t('agents.sensitivityCustom') :
            agent.sensitivity_preset || t('agents.sensitivityNotSet')
          }</p>
          <p><span className="font-medium">{t('agents.created')}:</span> {new Date(agent.created_at).toLocaleDateString()}</p>
        </div>
      </div>}

      {!agent.is_shared ? (
        <div className={`mt-auto flex items-center justify-between`}>
          <div className="flex space-x-2">
            <button
              onClick={() => handleToggleAgentStatus(agent.id, agent.is_active)}
              className={`text-sm font-medium ${
                agent.is_active
                  ? 'text-warning-600 hover:text-warning-500'
                  : 'text-success-600 hover:text-success-500'
              }`}
            >
              {agent.is_active ? t('agents.deactivate') : t('agents.activate')}
            </button>
            {!isChild && (can('agents.create') || isManagerOnly) && (
              <button
                onClick={() => navigate(`/agents/new?mode=child&parent=${agent.id}`)}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
                title={t('agents.addChildAgent', 'Add child agent')}
              >
                + {t('agents.addChildAgent', 'Add child')}
              </button>
            )}
          </div>
          <div className="flex space-x-2">
            {can('agents.edit') && canEditThisAgent && (
              <Link
                to={`/agents/${agent.id}/edit`}
                className="hover:text-foreground"
                style={{ color: 'var(--muted)' }}
                title={t('agents.editAgent')}
              >
                <PencilIcon className="h-5 w-5" />
              </Link>
            )}
            {can('agents.delete') && (
              <button
                onClick={() => openDeleteModal(agent)}
                className="hover:text-danger-600"
                style={{ color: 'var(--muted)' }}
                title={t('agents.deleteAgentTitle')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-auto flex items-center justify-end">
          {can('agents.edit') && canEditThisAgent ? (
            <Link
              to={`/agents/${agent.id}/edit`}
              className="hover:text-foreground"
              style={{ color: 'var(--muted)' }}
              title={t('agents.editAgent')}
            >
              <PencilIcon className="h-5 w-5" />
            </Link>
          ) : (
            <Link
              to={`/agents/${agent.id}/edit`}
              className="hover:text-foreground"
              style={{ color: 'var(--muted)' }}
              title={t('agents.viewAgent')}
            >
              <EyeIcon className="h-5 w-5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
            {t('agents.title')}
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {t('agents.subtitle')}
          </p>
        </div>
        {(can('agents.create') || isManagerOnly) && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={() => setShowChoiceModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('agents.newAgent')}
            </button>
          </div>
        )}
      </div>

      {/* Scope tabs */}
      {user?.salesperson_profile_id && (
        <div className="flex gap-2 border-b border-border pb-2">
          {['all', 'mine', 'org'].map(s => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                scope === s
                  ? 'bg-primary text-white'
                  : 'text-muted hover:text-foreground'
              }`}
            >
              {s === 'mine' ? t('agents.scopeMine', 'Mías')
                : s === 'org' ? t('agents.scopeOrg', 'Organización')
                : t('agents.scopeAll', 'Todos')}
            </button>
          ))}
        </div>
      )}

      {/* Agents List — nested hierarchy */}
      {agents.length === 0 ? (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t('agents.noAgents')}</h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('agents.getStarted')}</p>
          {(can('agents.create') || isManagerOnly) && (
            <div className="mt-6">
              <button
                onClick={() => setShowChoiceModal(true)}
                className="btn-primary inline-flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('agents.newAgent')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {topLevel.map((agent) => (
            <React.Fragment key={agent.id}>
              {/* Parent card or orphan child card (parent not in current list) */}
              <AgentCard
                agent={agent}
                isChild={!!agent.parent_agent_id}
                parentName={agentIndex[agent.parent_agent_id]?.name || ''}
              />
              {/* Children — indented, nested under parent.
                   col-span-full lets the row span the grid; inner div uses
                   calc() to subtract half the gap-6 (0.75rem at sm, 1rem at lg)
                   so the right edge stays flush with the parent column boundary. */}
              {(childMap[agent.id] || []).map((child) => {
                // Defensive: skip malformed child entries
                if (!child || !child.id) return null;
                return (
                <div key={child.id} className="col-span-full" style={{ marginTop: '-1rem' }}>
                  <div className="pl-8 sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]">
                    <AgentCard
                      agent={child}
                      isChild
                      parentName={agent.name}
                    />
                  </div>
                </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Choice Modal */}
      {showChoiceModal && (
        <CreateAgentChoiceModal
          onClose={() => setShowChoiceModal(false)}
          parentAgents={parentAgents}
          managerChildOnly={false}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAgent && (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-50" style={{ backgroundColor: 'var(--overlay)' }}>
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-xl bg-card">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-danger-100">
                <TrashIcon className="h-6 w-6 text-danger-600" />
              </div>
              <h3 className="text-lg font-medium text-foreground mt-4">{t('agents.deleteAgent')}</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm" style={{ color: 'var(--muted)' }}
                  dangerouslySetInnerHTML={{ __html: t('agents.deleteConfirm', { name: selectedAgent.name }) }}
                />
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="btn-secondary flex-1"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => handleDeleteAgent(selectedAgent.id)}
                    className="btn-danger flex-1"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agents;
